import { useMaintenanceCheck, MaintenanceScreen } from '../MaintenanceGate';
import { LeaveRosterEntry } from '../LeaveRoster';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const NAV_BLUE = '#1565c0';

function fmtNum(n) {
  if (n == null || n === '') return '0.00';
  const num = Number(n);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Gauge ─────────────────────────────────────────────────────
function Gauge({ pct }) {
  const clamp = Math.min(Math.max(Number(pct)||0, 0), 100);
  const cx=85, cy=78, r=56;
  const toRad = d => d * Math.PI / 180;
  const pctAngle = p => 180 - p * 1.8;
  function arc(p1, p2, rr) {
    const a1=pctAngle(p1), a2=pctAngle(p2);
    const x1=cx+rr*Math.cos(toRad(a1)), y1=cy-rr*Math.sin(toRad(a1));
    const x2=cx+rr*Math.cos(toRad(a2)), y2=cy-rr*Math.sin(toRad(a2));
    return `M${x1},${y1} A${rr},${rr} 0 0 1 ${x2},${y2}`;
  }
  function wedge(p1, p2, rr) {
    const a1=pctAngle(p1), a2=pctAngle(p2);
    const x1=cx+rr*Math.cos(toRad(a1)), y1=cy-rr*Math.sin(toRad(a1));
    const x2=cx+rr*Math.cos(toRad(a2)), y2=cy-rr*Math.sin(toRad(a2));
    return `M${cx},${cy} L${x1},${y1} A${rr},${rr} 0 0 1 ${x2},${y2} Z`;
  }
  function tickPt(p, offset=17) {
    const a=pctAngle(p);
    return { x: cx+(r+offset)*Math.cos(toRad(a)), y: cy-(r+offset)*Math.sin(toRad(a)) };
  }
  const vAngle = pctAngle(clamp);
  const nx = cx+44*Math.cos(toRad(vAngle)), ny = cy-44*Math.sin(toRad(vAngle));
  const color = clamp<=30?'#1e7e34':clamp<=50?'#b06000':'#c8102e';
  const tk0=tickPt(0,20), tk30=tickPt(30,17), tk50=tickPt(50,17), tk100=tickPt(100,20);
  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
      <svg width={200} height={125} viewBox="-15 -8 200 115">
        <path d={wedge(0,30,r)} fill="#1e7e34" opacity={0.13}/>
        <path d={arc(0,100,r)} fill="none" stroke="#e8e8e8" strokeWidth={12} strokeLinecap="round"/>
        <path d={arc(0,30,r)}   fill="none" stroke="#1e7e34" strokeWidth={12} strokeLinecap="round" opacity={0.5}/>
        <path d={arc(30,50,r)}  fill="none" stroke="#b06000" strokeWidth={12} strokeLinecap="round" opacity={0.3}/>
        <path d={arc(50,100,r)} fill="none" stroke="#c8102e" strokeWidth={12} strokeLinecap="round" opacity={0.25}/>
        {clamp>0 && <path d={arc(0,clamp,r)} fill="none" stroke={color} strokeWidth={12} strokeLinecap="round"/>}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#1a1a2e" strokeWidth={2.5} strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r={4} fill="#1a1a2e"/>
        <text x={tk0.x}   y={tk0.y+3}   textAnchor="end"    fontSize={8} fill="#999">0%</text>
        <text x={tk30.x}  y={tk30.y-2}  textAnchor="middle" fontSize={8} fill="#1e7e34" fontWeight="700">30%</text>
        <text x={tk50.x}  y={tk50.y-3}  textAnchor="middle" fontSize={8} fill="#b06000">50%</text>
        <text x={tk100.x} y={tk100.y+3} textAnchor="start"  fontSize={8} fill="#999">100%</text>
        <text x={cx} y={cy+20} textAnchor="middle" fontSize={15} fontWeight="700" fill={color}>{clamp.toFixed(1)}%</text>
      </svg>
      <div style={{fontSize:10,color:'#888',marginTop:2}}>% AR Overdue</div>
      <div style={{fontSize:10,color:'#1e7e34',marginTop:2}}>● Safe zone ≤ 30%</div>
    </div>
  );
}

// ── Outlet modal (read-only) ───────────────────────────────────
function OutletModal({ outlet, onClose }) {
  const groups = [
    { label: 'Pending Delivery', key: 'No Due Date', color: '#546e7a' },
    { label: 'Pre-due',          key: 'Predue',      color: '#b06000' },
    { label: 'Overdue',          key: 'Overdue',     color: '#c8102e' },
  ];
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#f0f2f5', width:'100%', maxWidth:700, maxHeight:'92vh', borderRadius:'16px 16px 0 0', overflow:'hidden', display:'flex', flexDirection:'column' }}>
        <div style={{ background:'#fff', padding:'16px 20px', borderBottom:'1px solid #f0f0f0', display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:'#1a1a2e' }}>{outlet.outletName}</div>
            <div style={{ fontSize:12, color:'#888', marginTop:2 }}>{outlet.outletId}</div>
          </div>
          <button style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#888', padding:0 }} onClick={onClose}>✕</button>
        </div>
        <div style={{ overflowY:'auto', padding:'12px 16px', flex:1 }}>
          {groups.map(g => {
            const rows = outlet.invoices.filter(inv => inv.arStatus === g.key);
            if (!rows.length) return null;
            return (
              <div key={g.key} style={{ background:'#fff', borderRadius:10, marginBottom:10, overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ padding:'10px 14px', borderLeft:`4px solid ${g.color}`, background:g.color+'12', fontSize:11, fontWeight:700, color:g.color, textTransform:'uppercase', letterSpacing:'0.5px' }}>{g.label}</div>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'#fafafa' }}>
                      <th style={{ padding:'8px 14px', textAlign:'left', color:'#888', fontWeight:600, borderBottom:'0.5px solid #eee' }}>Invoice ID</th>
                      <th style={{ padding:'8px 14px', textAlign:'right', color:'#888', fontWeight:600, borderBottom:'0.5px solid #eee' }}>Amount (RM)</th>
                      <th style={{ padding:'8px 14px', textAlign:'left', color:'#888', fontWeight:600, borderBottom:'0.5px solid #eee' }}>Supervisor Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((inv, i) => (
                      <tr key={i} style={{ borderBottom:'0.5px solid #f5f5f5' }}>
                        <td style={{ padding:'9px 14px', color:'#333', fontFamily:'monospace', fontSize:11 }}>{inv.invoiceId}</td>
                        <td style={{ padding:'9px 14px', textAlign:'right', color:'#1a1a2e', fontWeight:600, whiteSpace:'nowrap' }}>{fmtNum(inv.outstanding)}</td>
                        <td style={{ padding:'9px 14px', color:'#555', fontSize:11, maxWidth:220, whiteSpace:'pre-wrap', wordBreak:'break-word', lineHeight:1.5 }}>{inv.comment || <span style={{ color:'#ccc' }}>—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Sidebar ────────────────────────────────────────────────────
function ARSidebar({ open, onClose, navigate }) {
  if (!open) return null;
  return (
    <>
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:300 }} onClick={onClose} />
      <div style={{ position:'fixed', left:0, top:0, bottom:0, width:240, background:'#fff', zIndex:400, boxShadow:'4px 0 16px rgba(0,0,0,0.15)', display:'flex', flexDirection:'column' }}>
        <div style={{ background:NAV_BLUE, padding:'20px 16px 16px', color:'#fff' }}>
          <div style={{ fontSize:13, fontWeight:700 }}>Ninja Mart Credit</div>
          <div style={{ fontSize:11, opacity:0.7, marginTop:2 }}>Credit Management System</div>
        </div>
        <div style={{ flex:1, padding:'12px 0' }}>
          {[
            { label: 'Credit Assessment', path: '/', active: false },
            { label: 'AR Portfolio Monitoring',   path: '/ar', active: true  },
          ].map(item => (
            <div key={item.path}
              style={{ padding:'12px 20px', cursor:'pointer', fontSize:13, fontWeight:item.active?700:400, color:item.active?NAV_BLUE:'#555', background:item.active?NAV_BLUE+'12':'transparent', borderLeft:item.active?`3px solid ${NAV_BLUE}`:'3px solid transparent' }}
              onClick={e => { e.stopPropagation(); navigate(item.path); onClose(); }}>
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Stats card ─────────────────────────────────────────────────
const REGION_COLORS = { Central: '#1565c0', South: '#7b1fa2', North: '#b06000', 'East Coast': '#c8102e' };

function StatCard({ data, regionTab, dataByRegion }) {
  const totalAR      = data.reduce((s,r) => s+(Number(r.outstanding)||0), 0);
  const totalOverdue = data.filter(r=>r.arStatus==='Overdue').reduce((s,r) => s+(Number(r.outstanding)||0), 0);
  const pctOverdue   = totalAR>0 ? (totalOverdue/totalAR)*100 : 0;
  const uniqueOutlets = [...new Set(data.map(r=>r.outletId))];
  const isAll = regionTab === 'All Regions';

  // Per-region pct for legend
  const regionStats = isAll ? ['Central','South','North','East Coast'].map(r => {
    const rd = dataByRegion[r] || [];
    const rTotal   = rd.reduce((s,x) => s+(Number(x.outstanding)||0), 0);
    const rOverdue = rd.filter(x=>x.arStatus==='Overdue').reduce((s,x) => s+(Number(x.outstanding)||0), 0);
    const pct = rTotal>0 ? (rOverdue/rTotal)*100 : 0;
    return { region: r, pct };
  }) : [];

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1.6fr', gap:12, marginBottom:16 }}>
      {[
        { val:uniqueOutlets.length, lbl:'Active Outlets', big:true,  color:NAV_BLUE  },
        { val:fmtNum(totalAR),      lbl:'Total AR',       big:false, color:'#1a1a2e' },
        { val:fmtNum(totalOverdue), lbl:'Total AR Overdue', big:false, color:'#c8102e' },
      ].map((st,i) => (
        <div key={i} style={{ background:'#fff', borderRadius:10, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', padding:'16px 20px', borderTop:`3px solid ${st.color}` }}>
          {st.big
            ? <div style={{ fontSize:36, fontWeight:700, color:st.color }}>{st.val}</div>
            : <div><div style={{ fontSize:11, color:'#aaa' }}>RM</div><div style={{ fontSize:18, fontWeight:700, color:st.color, lineHeight:1.2 }}>{st.val}</div></div>
          }
          <div style={{ fontSize:12, color:'#888', marginTop:6 }}>{st.lbl}</div>
        </div>
      ))}
      <div style={{ background:pctOverdue<=30?'#fff':'#fff0f2', borderRadius:10, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', padding:'16px 20px', display:'flex', alignItems:'center', justifyContent:'center', gap:12, borderTop:`3px solid ${pctOverdue<=30?'#1e7e34':'#c8102e'}` }}>
        <Gauge pct={pctOverdue} />
        {isAll && regionStats.length > 0 && (
          <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
            <div style={{ fontSize:10, color:'#888', fontWeight:600, marginBottom:2 }}>By Region</div>
            {regionStats.map(rs => {
              const c = rs.pct<=30?'#1e7e34':rs.pct<=50?'#b06000':'#c8102e';
              return (
                <div key={rs.region} style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <span style={{ width:8, height:8, borderRadius:'50%', background:REGION_COLORS[rs.region], flexShrink:0, display:'inline-block' }}/>
                  <span style={{ fontSize:10, color:'#555', width:68 }}>{rs.region}</span>
                  <span style={{ fontSize:10, fontWeight:700, color:c }}>{rs.pct.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
const REGIONS = ['All Regions', 'Central', 'South', 'North', 'East Coast'];
const AR_SHEET_ID = '138bedRKFcsqqpTzyEi7QYLVZmLksCkKX8QY3dQVIyGs';

export default function ARDashboard() {
  const { user, logout } = useAuth();
  const maint = useMaintenanceCheck('approver');
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen]   = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen]   = useState(false);
  const [regionTab, setRegionTab]       = useState('All Regions');
  const [dataByRegion, setDataByRegion] = useState({});
  const [refreshKey, setRefreshKey]       = useState(0);
  const [loading, setLoading]           = useState({});
  const [openOutlet, setOpenOutlet]     = useState(null);
  const [fOutletId,   setFOutletId]     = useState('');
  const [fOutletName, setFOutletName]   = useState('');
  const [fVanId,      setFVanId]        = useState('');
  const [fStatus,     setFStatus]       = useState('');

  useEffect(() => {
    if (!profileOpen) return;
    const close = () => setProfileOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [profileOpen]);

  async function loadRegion(region, force = false) {
    setLoading(prev => {
      if (!force && (prev[region] === true)) return prev;
      return { ...prev, [region]: true };
    });
    try {
      // 1A consolidation: the East Coast tab shows all three East Coast regions
      const apiRegion = region === 'East Coast' ? 'East Coast,East Coast 1,East Coast 2' : region;
      const res = await fetch(`/api/ar/data?region=${encodeURIComponent(apiRegion)}`);
      if (!res.ok) throw new Error(await res.text());
      const d = await res.json();
      setDataByRegion(prev => ({ ...prev, [region]: d }));
    } catch(e) {
      setDataByRegion(prev => ({ ...prev, [region]: [] }));
    }
    setLoading(prev => ({ ...prev, [region]: false }));
  }

  useEffect(() => {
    setDataByRegion({});
    setLoading({});
    activeRegions.forEach(r => loadRegion(r, true));
  }, [refreshKey]);

  const activeRegions = ['Central', 'South', 'North', 'East Coast'];

  const currentData = useMemo(() => {
    if (regionTab === 'All Regions') {
      return activeRegions.flatMap(r => dataByRegion[r] || []);
    }
    return dataByRegion[regionTab] || [];
  }, [regionTab, dataByRegion]);

  const isLoading = regionTab === 'All Regions'
    ? activeRegions.some(r => loading[r])
    : !!loading[regionTab];

  const byOutlet = useMemo(() => {
    const map = {};
    currentData.forEach(r => {
      if (!map[r.outletId]) map[r.outletId] = { outletId:r.outletId, outletName:r.outletName, vanId:r.vanId, invoices:[], region: r.region };
      map[r.outletId].invoices.push(r);
    });
    return Object.values(map).map(o => {
      const pending = o.invoices.filter(r=>r.arStatus==='No Due Date').reduce((s,r)=>s+(Number(r.outstanding)||0),0);
      const predue  = o.invoices.filter(r=>r.arStatus==='Predue').reduce((s,r)=>s+(Number(r.outstanding)||0),0);
      const overdue = o.invoices.filter(r=>r.arStatus==='Overdue').reduce((s,r)=>s+(Number(r.outstanding)||0),0);
      return { ...o, pending, predue, overdue, total:pending+predue+overdue };
    }).filter(o => {
      if (fOutletId   && !o.outletId.toUpperCase().includes(fOutletId.toUpperCase()))    return false;
      if (fOutletName && !o.outletName.toUpperCase().includes(fOutletName.toUpperCase())) return false;
      if (fVanId) {
        const vans = o.invoices.flatMap(inv=>(inv.vanId||'').split('|').map(v=>v.trim()).filter(Boolean));
        if (!vans.some(v=>v.toUpperCase().includes(fVanId.toUpperCase()))) return false;
      }
      if (fStatus==='No Due Date' && o.pending<=0) return false;
      if (fStatus==='Predue'      && o.predue<=0)  return false;
      if (fStatus==='Overdue'     && o.overdue<=0) return false;
      return true;
    }).sort((a,b) => b.overdue-a.overdue);
  }, [currentData, fOutletId, fOutletName, fVanId, fStatus]);

  const openOutletData = openOutlet ? byOutlet.find(o=>o.outletId===openOutlet) : null;

  const s = {
    th: { padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#555', textTransform:'uppercase', letterSpacing:'0.4px', borderBottom:'2px solid #e0e0e0', background:'#f0f4f8', whiteSpace:'nowrap', position:'sticky', top:0, zIndex:2 },
    td: { padding:'12px 14px', fontSize:13, borderBottom:'1px solid #f0f0f0', verticalAlign:'middle' },
  };

  if (maint.blocked) return <MaintenanceScreen />;
  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', fontFamily:'system-ui,-apple-system,sans-serif' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <ARSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} navigate={navigate} />

      {/* Nav */}
      <nav style={{ background:NAV_BLUE, padding:'10px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:6, padding:'6px 8px', cursor:'pointer', color:'#fff', fontSize:16, lineHeight:1 }}
            onClick={() => setSidebarOpen(true)}>☰</button>
          <img
  src="/logo-wordmark.svg"
  alt="Ninja Mart"
  width={26 * 3.84}
  height={26}
  style={{ height: 26, width: 26 * 3.84, objectFit: 'contain' }}
  onError={e => { e.target.style.display = 'none'; }}
/>
          <div style={{ color:'#fff', fontWeight:700, fontSize:15 }}>AR Portfolio Monitoring</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ position:'relative' }}>
            <div style={{ textAlign:'right', cursor:'pointer', userSelect:'none' }}
              onClick={e => { e.stopPropagation(); setProfileOpen(p=>!p); }}>
              <div style={{ fontWeight:600, color:'#fff', fontSize:13 }}>{user.displayName} ▾</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)', marginTop:1 }}>L{user.level} Approver</div>
            </div>
            {profileOpen && (
              <div style={{ position:'absolute', top:'100%', right:0, marginTop:8, background:'#fff', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.15)', minWidth:160, zIndex:200, overflow:'hidden' }}>
                <button style={{ width:'100%', padding:'12px 16px', background:'none', border:'none', textAlign:'left', cursor:'pointer', fontSize:13, color:'#333' }}
                  onClick={e => { e.stopPropagation(); setProfileOpen(false); setLeaveModalOpen(true); }}>Set Leave Roster</button>
                <button style={{ width:'100%', padding:'12px 16px', background:'none', border:'none', textAlign:'left', cursor:'pointer', fontSize:13, color:'#c5221f', fontWeight:600 }}
                  onClick={e => { e.stopPropagation(); setProfileOpen(false); logout(); }}>Sign out</button>
              </div>
            )}
            <LeaveRosterEntry open={leaveModalOpen} onClose={() => setLeaveModalOpen(false)} />
          </div>
        </div>
      </nav>

      {/* Region tabs */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e0e0e0', padding:'0 20px', display:'flex', gap:0 }}>
        {REGIONS.map(r => (
          <button key={r}
            style={{ padding:'12px 18px', border:'none', background:'transparent', cursor:'pointer', fontSize:13, fontWeight:regionTab===r?700:400, color:regionTab===r?NAV_BLUE:'#888', borderBottom:regionTab===r?`2px solid ${NAV_BLUE}`:'2px solid transparent', transition:'all 0.1s' }}
            onClick={() => { setRegionTab(r); if (r !== 'All Regions') loadRegion(r); }}>
            {r}
          </button>
        ))}
      </div>

      <div style={{ maxWidth:1200, margin:'0 auto', padding:'20px 16px' }}>
        {/* Stats */}
        <StatCard data={currentData} regionTab={regionTab} dataByRegion={dataByRegion} />

        {/* Filters */}
        <div style={{ background:'#fff', borderRadius:10, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', padding:'12px 18px', marginBottom:12, display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
          {[
            { label:'Outlet ID',   val:fOutletId,   set:setFOutletId,   ph:'Search Outlet ID...'   },
            { label:'Outlet Name', val:fOutletName, set:setFOutletName, ph:'Search Outlet Name...' },
            { label:'Van ID',      val:fVanId,      set:setFVanId,      ph:'Search Van ID...'      },
          ].map(f => (
            <div key={f.label} style={{ display:'flex', flexDirection:'column', gap:3 }}>
              <span style={{ fontSize:11, color:'#888', fontWeight:600 }}>{f.label}</span>
              <input style={{ padding:'6px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, width:140 }}
                placeholder={f.ph} value={f.val} onChange={e => f.set(e.target.value)} />
            </div>
          ))}
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            <span style={{ fontSize:11, color:'#888', fontWeight:600 }}>AR Status</span>
            <select style={{ padding:'6px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, background:'#fff' }}
              value={fStatus} onChange={e => setFStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="No Due Date">Pending Delivery</option>
              <option value="Predue">Pre-due</option>
              <option value="Overdue">Overdue</option>
            </select>
          </div>
          <button style={{ padding:'6px 14px', background:'#f0f4f8', border:'1px solid #ddd', borderRadius:6, cursor:'pointer', fontSize:12, color:'#555', alignSelf:'flex-end' }}
            onClick={() => { setFOutletId(''); setFOutletName(''); setFVanId(''); setFStatus(''); }}>
            Clear
          </button>
        </div>

        {/* Table */}
        <div style={{ background:'#fff', borderRadius:10, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 18px', borderBottom:'1px solid #f0f0f0' }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#1a1a2e' }}>AR Outlet List</div>
            <button style={{ background:'#f0f4f8', border:'1px solid #ddd', borderRadius:6, padding:'6px 14px', cursor:'pointer', fontSize:12, color:'#555' }}
              onClick={() => setRefreshKey(k => k + 1)}>↻ Refresh</button>
          </div>
          {isLoading ? (
            <div style={{ padding:48, textAlign:'center', color:'#888', fontSize:13 }}>
              <div style={{ width:24, height:24, border:`3px solid #eee`, borderTopColor:NAV_BLUE, borderRadius:'50%', animation:'spin 0.7s linear infinite', margin:'0 auto 12px' }} />
              Loading...
            </div>
          ) : byOutlet.length===0 ? (
            <div style={{ padding:48, textAlign:'center', color:'#888', fontSize:13 }}>No AR records</div>
          ) : (
            <div style={{ overflowX:'auto', overflowY:'auto', maxHeight:'calc(100vh - 420px)' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    <th style={s.th}>Outlet ID</th>
                    <th style={{ ...s.th, minWidth:200 }}>Outlet Name</th>
                    {regionTab === 'All Regions' && <th style={s.th}>Region</th>}
                    <th style={{ ...s.th, minWidth:100 }}>Van ID</th>
                    <th style={{ ...s.th, textAlign:'right', color:'#546e7a' }}>Pending Delivery</th>
                    <th style={{ ...s.th, textAlign:'right', color:'#b06000' }}>Pre-due</th>
                    <th style={{ ...s.th, textAlign:'right', color:'#c8102e' }}>Overdue</th>
                    <th style={{ ...s.th, textAlign:'right' }}>Total AR</th>
                  </tr>
                </thead>
                <tbody>
                  {byOutlet.map(o => {
                    // Get region for this outlet
                    const outletRegion = o.invoices[0]?.region || '';
                    return (
                      <tr key={o.outletId}
                        style={{ cursor:'pointer', borderLeft:o.overdue>0?'3px solid #c8102e':'3px solid #e0f0ff' }}
                        onClick={() => setOpenOutlet(o.outletId)}
                        onMouseEnter={e => e.currentTarget.style.background='#f0f4f8'}
                        onMouseLeave={e => e.currentTarget.style.background=''}>
                        <td style={s.td}>{o.outletId}</td>
                        <td style={{ ...s.td, minWidth:200 }}>{o.outletName}</td>
                        {regionTab === 'All Regions' && <td style={s.td}>{outletRegion || '—'}</td>}
                        <td style={{ ...s.td, minWidth:100, fontSize:12, color:'#888' }}>
                          {[...new Set(o.invoices.flatMap(inv=>(inv.vanId||'').split('|').map(v=>v.trim()).filter(Boolean)))].map((v,i)=><div key={i}>{v}</div>)}
                        </td>
                        <td style={{ ...s.td, textAlign:'right', color:'#546e7a' }}>{o.pending>0?fmtNum(o.pending):'—'}</td>
                        <td style={{ ...s.td, textAlign:'right', color:'#b06000' }}>{o.predue>0?fmtNum(o.predue):'—'}</td>
                        <td style={{ ...s.td, textAlign:'right', color:o.overdue>0?'#c8102e':'#aaa', fontWeight:o.overdue>0?700:400 }}>{o.overdue>0?fmtNum(o.overdue):'—'}</td>
                        <td style={{ ...s.td, textAlign:'right', fontWeight:600 }}>{fmtNum(o.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {openOutletData && (
        <OutletModal outlet={openOutletData} onClose={() => setOpenOutlet(null)} />
      )}
    </div>
  );
}